import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

function extractYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

// YouTube player component using IFrame API
function YouTubePlayer({ videoId, shouldLoop, onEnded }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const readyRef = useRef(false);

  useEffect(() => {
    // Load YouTube IFrame API if not loaded
    if (!window.YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
    }

    const initPlayer = () => {
      if (!containerRef.current) return;
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId,
        playerVars: { autoplay: 1, mute: 1, controls: 0, showinfo: 0, rel: 0, loop: shouldLoop ? 1 : 0, playlist: shouldLoop ? videoId : undefined },
        events: {
          onReady: () => { readyRef.current = true; },
          onStateChange: (e) => {
            // State 0 = ended
            if (e.data === 0 && !shouldLoop && onEnded) {
              onEnded();
            }
          }
        }
      });
    };

    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      window.onYouTubeIframeAPIReady = initPlayer;
    }

    return () => {
      if (playerRef.current?.destroy) {
        try { playerRef.current.destroy(); } catch (e) {}
      }
    };
  }, [videoId, shouldLoop, onEnded]);

  return <div ref={containerRef} style={{ width: "100%", height: "100%" }} />;
}

function ContentSlide({ item, orientation, onVideoEnded }) {
  const isPortrait = orientation === "portrait";

  if (item.tipo === "video") {
    const ytId = extractYouTubeId(item.url);
    if (ytId) {
      const shouldLoop = item.duracion !== 0;
      return (
        <div className="w-full h-full bg-black flex items-center justify-center">
          <YouTubePlayer videoId={ytId} shouldLoop={shouldLoop} onEnded={onVideoEnded} />
        </div>
      );
    }
    const shouldLoop = item.duracion !== 0;
    return (
      <div className="w-full h-full bg-black flex items-center justify-center">
        <video src={item.url} autoPlay muted loop={shouldLoop} onEnded={onVideoEnded} className="w-full h-full object-contain" />
      </div>
    );
  }

  if (item.tipo === "imagen") {
    return (
      <div className="w-full h-full bg-black flex items-center justify-center">
        <img src={item.url} alt={item.titulo} className="w-full h-full object-contain" />
      </div>
    );
  }

  if (item.tipo === "texto" || item.tipo === "bienvenida") {
    const bgColor = item.config?.bgColor || "#1e293b";
    const textColor = item.config?.textColor || "#ffffff";
    const isBienvenida = item.tipo === "bienvenida";
    const hasHtml = item.texto && (item.texto.includes("<") && item.texto.includes(">"));
    
    // Font size scaling based on config
    const sizeMap = { small: 0.6, normal: 1, large: 1.4, xlarge: 1.8 };
    const scale = sizeMap[item.config?.fontSize] || 1;
    const titleSize = isPortrait ? `${7 * scale}vmin` : `${5 * scale}vmin`;
    const textSize = isPortrait ? `${5 * scale}vmin` : `${3.5 * scale}vmin`;
    const subSize = isPortrait ? `${3 * scale}vmin` : `${2 * scale}vmin`;
    
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: bgColor, padding: isPortrait ? "2rem" : "3rem" }}>
        <div className="text-center" style={{ maxWidth: isPortrait ? "90vw" : "80vw" }}>
          {isBienvenida && (
            <p style={{ color: textColor, opacity: 0.7, fontSize: subSize, marginBottom: "1.5vmin" }}>
              {item.config?.subtitulo || "Le damos la bienvenida"}
            </p>
          )}
          {item.titulo && (
            <h2 style={{ color: textColor, fontSize: titleSize, fontWeight: 700, marginBottom: "2vmin", lineHeight: 1.2 }}>
              {item.titulo}
            </h2>
          )}
          {item.texto && (
            hasHtml ? (
              <div 
                style={{ color: textColor, fontSize: textSize, lineHeight: 1.5, opacity: isBienvenida ? 0.8 : 1 }}
                dangerouslySetInnerHTML={{ __html: item.texto }} 
              />
            ) : (
              <p style={{ color: textColor, fontSize: textSize, lineHeight: 1.5, whiteSpace: "pre-wrap", opacity: isBienvenida ? 0.8 : 1 }}>
                {item.texto}
              </p>
            )
          )}
          {isBienvenida && item.config?.empresa && (
            <p style={{ color: textColor, opacity: 0.5, fontSize: isPortrait ? `${2.5 * scale}vmin` : `${1.8 * scale}vmin`, marginTop: "3vmin" }}>
              {item.config.empresa}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (item.tipo === "sala") {
    const ocupada = item.config?.ocupada;
    const bgColor = ocupada ? "#dc2626" : "#16a34a";
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: bgColor, padding: "3rem" }}>
        <div className="text-center text-white">
          <div style={{ fontSize: isPortrait ? "4vmin" : "2.5vmin", opacity: 0.8, marginBottom: "2vmin" }}>
            {item.config?.ubicacion || "Sala de Conferencias"}
          </div>
          <h2 style={{ fontSize: isPortrait ? "8vmin" : "6vmin", fontWeight: 700, marginBottom: "2vmin" }}>
            {item.titulo || "Sala Principal"}
          </h2>
          <div style={{ fontSize: isPortrait ? "6vmin" : "4vmin", fontWeight: 600, marginBottom: "2vmin" }}>
            {ocupada ? "OCUPADA" : "DISPONIBLE"}
          </div>
          {ocupada && item.texto && (
            <p style={{ fontSize: isPortrait ? "3.5vmin" : "2.5vmin", opacity: 0.9 }}>{item.texto}</p>
          )}
          {ocupada && item.config?.hasta && (
            <p style={{ fontSize: isPortrait ? "3vmin" : "2vmin", opacity: 0.7, marginTop: "1vmin" }}>
              Hasta las {item.config.hasta}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (item.tipo === "link") {
    return (
      <div className="w-full h-full">
        <iframe src={item.url} className="w-full h-full border-0" title={item.titulo || "Web"} />
      </div>
    );
  }

  return <div className="w-full h-full bg-slate-900 flex items-center justify-center text-white">Contenido no soportado</div>;
}

export default function SignageDisplayPage() {
  const { token } = useParams();
  const [canal, setCanal] = useState(null);
  const [contenido, setContenido] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [orientation, setOrientation] = useState("landscape");
  const [started, setStarted] = useState(false);
  const timerRef = useRef(null);
  const controlsTimerRef = useRef(null);
  const touchStartRef = useRef(null);

  // Check if embedded (skip start screen)
  const isEmbedded = typeof window !== "undefined" && window.location.search.includes("embedded=1");

  // DISPLAY_ID único por pantalla (persistido en localStorage), permite monitorearla desde el programador
  const displayIdRef = useRef(null);
  if (!displayIdRef.current && typeof window !== "undefined") {
    try {
      let did = localStorage.getItem("signage_display_id");
      if (!did) {
        did = "disp-" + Math.random().toString(36).slice(2, 10) + "-" + Date.now().toString(36);
        localStorage.setItem("signage_display_id", did);
      }
      displayIdRef.current = did;
    } catch (e) {
      displayIdRef.current = "disp-" + Math.random().toString(36).slice(2, 12);
    }
  }

  // Detect orientation
  useEffect(() => {
    const checkOrientation = () => {
      setOrientation(window.innerHeight > window.innerWidth ? "portrait" : "landscape");
    };
    checkOrientation();
    window.addEventListener("resize", checkOrientation);
    if (screen.orientation) {
      screen.orientation.addEventListener("change", checkOrientation);
    }
    return () => {
      window.removeEventListener("resize", checkOrientation);
      if (screen.orientation) {
        screen.orientation.removeEventListener("change", checkOrientation);
      }
    };
  }, []);

  // Auto fullscreen on load + ESC closes window (but NOT in kiosk mode)
  useEffect(() => {
    // ESC exits fullscreen then closes window — disabled in kiosk mode so the TV never goes blank
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement && started) {
        if (canal?.skip_start_screen) {
          // Kiosk mode: keep the display running; re-enter fullscreen at next gesture via auto_fullscreen effect
          return;
        }
        try { window.close(); } catch (e) {}
      }
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    // Hide cursor after 3s of no movement
    let cursorTimer;
    const hideCursor = () => { document.body.style.cursor = "none"; };
    const showCursor = () => {
      document.body.style.cursor = "default";
      clearTimeout(cursorTimer);
      cursorTimer = setTimeout(hideCursor, 3000);
    };
    if (started) {
      document.addEventListener("mousemove", showCursor);
      hideCursor();
    }

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mousemove", showCursor);
      clearTimeout(cursorTimer);
    };
  }, [started, canal]);

  const handleStart = async () => {
    try {
      const el = document.documentElement;
      if (el.requestFullscreen) await el.requestFullscreen();
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      else if (el.msRequestFullscreen) await el.msRequestFullscreen();
    } catch (e) { /* some browsers may block */ }
    setStarted(true);
  };

  const toggleFullscreen = useCallback(async () => {
    try {
      const isFs = document.fullscreenElement || document.webkitFullscreenElement;
      if (isFs) {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
      } else {
        const el = document.documentElement;
        if (el.requestFullscreen) await el.requestFullscreen();
        else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      }
    } catch (e) { /* ignore */ }
  }, []);

  const fetchContent = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/signage/display/${token}`);
      if (!res.ok) throw new Error("Canal no encontrado");
      const data = await res.json();
      setCanal(data.canal);
      // Set page title to canal name
      document.title = data.canal?.nombre || "Cartelera Digital";
      // Filter by schedule
      const now = new Date();
      const currentHHMM = now.getHours() * 60 + now.getMinutes();
      const filtered = (data.contenido || []).filter(item => {
        if (!item.config?.horario_inicio && !item.config?.horario_fin) return true;
        const parseHM = (s) => { if (!s) return null; const [h, m] = s.split(":").map(Number); return h * 60 + (m || 0); };
        const start = parseHM(item.config?.horario_inicio);
        const end = parseHM(item.config?.horario_fin);
        if (start !== null && currentHHMM < start) return false;
        if (end !== null && currentHHMM > end) return false;
        return true;
      });
      setContenido(filtered);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchContent(); }, [fetchContent]);
  useEffect(() => {
    const interval = setInterval(fetchContent, 30000);
    return () => clearInterval(interval);
  }, [fetchContent]);

  // HEARTBEAT: reportar estado al backend cada 60s (y uno inmediato al iniciar)
  useEffect(() => {
    if (isEmbedded || !canal?.id) return; // no heartbeat para previews embebidos
    const sendHeartbeat = async () => {
      try {
        const currentItem = contenido[currentIndex];
        const body = {
          display_id: displayIdRef.current,
          resolution: `${window.screen?.width || window.innerWidth}x${window.screen?.height || window.innerHeight}`,
          orientation,
          current_item_id: currentItem?.id || "",
          current_item_titulo: currentItem?.titulo || "",
          paused,
          user_agent: navigator.userAgent,
        };
        await fetch(`${API_URL}/api/signage/display/${token}/heartbeat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      } catch (e) { /* silent */ }
    };
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 60000);
    return () => clearInterval(interval);
  }, [canal, token, contenido, currentIndex, orientation, paused, isEmbedded]);

  // KIOSK MODE: auto-skip start screen when canal.skip_start_screen = true
  useEffect(() => {
    if (canal?.skip_start_screen && !started && !isEmbedded) {
      setStarted(true);
    }
  }, [canal, started, isEmbedded]);

  // KIOSK MODE: auto-enter fullscreen on first user gesture when canal.auto_fullscreen = true
  useEffect(() => {
    if (!canal?.auto_fullscreen || isEmbedded) return;
    if (document.fullscreenElement || document.webkitFullscreenElement) return;
    let triggered = false;
    const tryFullscreen = async () => {
      if (triggered) return;
      triggered = true;
      try {
        const el = document.documentElement;
        if (el.requestFullscreen) await el.requestFullscreen();
        else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      } catch (e) { /* user may have blocked */ }
    };
    const evts = ["click", "keydown", "touchstart", "mousedown"];
    evts.forEach(e => document.addEventListener(e, tryFullscreen, { once: true, passive: true }));
    return () => {
      evts.forEach(e => document.removeEventListener(e, tryFullscreen));
    };
  }, [canal, isEmbedded]);

  // Keyboard shortcuts on display: F, Space, arrows, Esc
  useEffect(() => {
    if (!started || isEmbedded) return;
    const onKey = (e) => {
      // Ignore when typing in an input
      if (["INPUT", "TEXTAREA"].includes(e.target?.tagName)) return;
      const k = (e.key || "").toLowerCase();
      if (k === "f") { e.preventDefault(); toggleFullscreen(); }
      else if (k === " " || k === "spacebar") { e.preventDefault(); setPaused(p => !p); }
      else if (k === "arrowright") {
        if (contenido.length > 0) setCurrentIndex(prev => (prev + 1) % contenido.length);
      }
      else if (k === "arrowleft") {
        if (contenido.length > 0) setCurrentIndex(prev => (prev - 1 + contenido.length) % contenido.length);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [started, isEmbedded, contenido.length, toggleFullscreen]);

  // Auto-advance
  useEffect(() => {
    if (contenido.length <= 1 || paused) return;
    const current = contenido[currentIndex];
    if (!current) return;
    // duracion 0 = full video duration, skip timer (advance via onVideoEnded)
    if (current.duracion === 0 && current.tipo === "video") return;
    const duration = (current.duracion || 10) * 1000;
    timerRef.current = setTimeout(() => {
      setCurrentIndex(prev => (prev + 1) % contenido.length);
    }, duration);
    return () => clearTimeout(timerRef.current);
  }, [currentIndex, contenido, paused]);

  const handleVideoEnded = useCallback(() => {
    if (contenido.length > 1) {
      setCurrentIndex(prev => (prev + 1) % contenido.length);
    }
  }, [contenido.length]);

  // Keyboard controls
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        setCurrentIndex(prev => (prev + 1) % contenido.length);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        setCurrentIndex(prev => (prev - 1 + contenido.length) % contenido.length);
      } else if (e.key === "p" || e.key === "P") {
        setPaused(prev => !prev);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [contenido.length]);

  // Touch/click controls - show controls on tap, swipe to navigate
  const handleTouchStart = (e) => {
    touchStartRef.current = { x: e.touches?.[0]?.clientX || e.clientX, time: Date.now() };
  };

  const handleTouchEnd = (e) => {
    if (!touchStartRef.current) return;
    const endX = e.changedTouches?.[0]?.clientX || e.clientX;
    const diff = endX - touchStartRef.current.x;
    const elapsed = Date.now() - touchStartRef.current.time;
    if (Math.abs(diff) > 50 && elapsed < 500) {
      if (diff < 0) setCurrentIndex(prev => (prev + 1) % contenido.length);
      else setCurrentIndex(prev => (prev - 1 + contenido.length) % contenido.length);
    } else if (elapsed < 300) {
      setShowControls(true);
      clearTimeout(controlsTimerRef.current);
      controlsTimerRef.current = setTimeout(() => setShowControls(false), 4000);
    }
    touchStartRef.current = null;
  };

  if (loading) {
    return (
      <div className="w-screen h-screen bg-slate-900 flex items-center justify-center" style={{ height: "100dvh" }}>
        <div className="text-center text-white">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p style={{ fontSize: "2.5vmin" }}>Cargando cartelera...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-screen h-screen bg-slate-900 flex items-center justify-center" style={{ height: "100dvh" }}>
        <div className="text-center text-white">
          <p style={{ fontSize: "3vmin" }}>Canal no disponible</p>
          <p style={{ fontSize: "2vmin", color: "#94a3b8" }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!started && !isEmbedded) {
    return (
      <div className="w-screen h-screen bg-slate-900 flex items-center justify-center" style={{ height: "100dvh" }}>
        <button
          onClick={handleStart}
          className="text-center group"
          style={{ background: "none", border: "none", cursor: "pointer" }}
        >
          <div style={{ width: "12vmin", height: "12vmin", borderRadius: "50%", background: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 2vmin", transition: "transform 0.2s" }} className="group-hover:scale-110">
            <svg width="5vmin" height="5vmin" viewBox="0 0 24 24" fill="white" style={{ width: "5vmin", height: "5vmin", marginLeft: "0.5vmin" }}><polygon points="5 3 19 12 5 21 5 3"/></svg>
          </div>
          <p style={{ fontSize: "3.5vmin", fontWeight: 300, color: "#fff", marginBottom: "1vmin" }}>{canal?.nombre || "Cartelera Digital"}</p>
          <p style={{ fontSize: "1.8vmin", color: "#64748b" }}>Toca para iniciar en pantalla completa</p>
        </button>
      </div>
    );
  }

  if (contenido.length === 0) {
    return (
      <div className="w-screen h-screen bg-slate-900 flex items-center justify-center" style={{ height: "100dvh" }}>
        <div className="text-center text-white">
          <p style={{ fontSize: "4vmin", fontWeight: 300 }}>{canal?.nombre}</p>
          <p style={{ fontSize: "2vmin", color: "#94a3b8" }}>No hay contenido programado para este horario</p>
        </div>
      </div>
    );
  }

  const currentItem = contenido[currentIndex];

  return (
    <div
      className="w-screen overflow-hidden bg-black relative"
      style={{ height: "100dvh", cursor: showControls ? "default" : "none" }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleTouchStart}
      onMouseUp={handleTouchEnd}
    >
      <ContentSlide item={currentItem} orientation={orientation} onVideoEnded={handleVideoEnded} />

      {/* Pause indicator */}
      {paused && (
        <div className="absolute top-4 right-4 bg-black/60 text-white rounded-full px-3 py-1" style={{ fontSize: "1.5vmin" }}>
          PAUSADO
        </div>
      )}

      {/* On-screen controls (shown on tap/click) */}
      {showControls && contenido.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-black/70 rounded-full px-4 py-2 backdrop-blur-sm">
          <button onClick={() => setCurrentIndex(prev => (prev - 1 + contenido.length) % contenido.length)} className="text-white/80 hover:text-white p-2">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button onClick={() => setPaused(prev => !prev)} className="text-white/80 hover:text-white p-2">
            {paused ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
            )}
          </button>
          <button onClick={() => setCurrentIndex(prev => (prev + 1) % contenido.length)} className="text-white/80 hover:text-white p-2">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <span className="text-white/50 text-xs ml-2">{currentIndex + 1}/{contenido.length}</span>
        </div>
      )}

      {/* Progress bar */}
      {contenido.length > 1 && (
        <div className="absolute bottom-0 left-0 right-0 flex gap-0.5 p-1 bg-black/20">
          {contenido.map((_, i) => (
            <div key={i} className={`h-0.5 flex-1 rounded-full transition-colors ${i === currentIndex ? "bg-white" : "bg-white/20"}`} />
          ))}
        </div>
      )}
    </div>
  );
}
